import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { FolderOpen } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import DashboardLayout from '../components/DashboardLayout';
import { apiService } from '../services/api';
import { useAppDispatch, useAppSelector } from '../store';
import {
  loadParserProfiles,
  fetchTasks,
  persistProfileSelection,
  setTasks,
  setFolderName,
  setShowPreview,
  setFromDate,
  setToDate,
  setSubmitting,
  setError,
} from '../store/timekeepingSlice';
import type { TaskResponse } from '../types/timekeeping';

// ── Session-scoped staging ID ────────────────────────────────────────────────
const SESSION_STAGING_KEY = 'timekeeping_staging_session_id';

function getStagingSessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_STAGING_KEY);
    if (!id) {
      id = uuidv4();
      sessionStorage.setItem(SESSION_STAGING_KEY, id);
    }
    return id;
  } catch {
    return uuidv4();
  }
}

// ── IndexedDB helpers for persisting folder handle across sessions ────────────
const IDB_NAME = 'cranberry_timekeeping';
const IDB_STORE = 'folder_handles';

function openIDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(IDB_STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function saveHandleIDB(key: string, handle: FileSystemDirectoryHandle): Promise<void> {
  try {
    const db = await openIDB();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(handle, key);
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); reject(tx.error); };
    });
  } catch { /* non-fatal */ }
}

async function loadHandleIDB(key: string): Promise<FileSystemDirectoryHandle | null> {
  try {
    const db = await openIDB();
    return await new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = () => { db.close(); resolve(req.result ?? null); };
      req.onerror = () => { db.close(); resolve(null); };
    });
  } catch {
    return null;
  }
}

// ── Constants ────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 5000;
const TASK_TIMEOUT_MS = 15 * 60 * 1000;
const TERMINAL = new Set(['COMPLETED', 'FAILED', 'CANCELLED', 'WARNING']);

function needsPolling(t: TaskResponse): boolean {
  if (TERMINAL.has(t.status)) return false;
  return Date.now() - new Date(t.createdAt).getTime() < TASK_TIMEOUT_MS;
}

function expectedFilename(fileNameFormat: string, date: Dayjs): string {
  return fileNameFormat
    .replace('{YYYY}', date.format('YYYY'))
    .replace('{MM}', date.format('MM'))
    .replace('{DD}', date.format('DD'));
}

function buildLabel(from: Dayjs, to: Dayjs): string {
  const fromStr = from.format('YYYY-MM-DD');
  const toStr = to.format('YYYY-MM-DD');
  return fromStr === toStr ? `Import | ${fromStr}` : `Import | From ${fromStr} to ${toStr}`;
}

function statusColor(status: string): 'success' | 'error' | 'warning' | 'info' | 'default' {
  if (status === 'COMPLETED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'WARNING') return 'warning';
  if (status === 'PROCESSING' || status === 'PENDING') return 'info';
  return 'default';
}

function folderIDBKey(user: string) { return `folder_handle_${user}`; }
function folderNameKey(user: string) { return `timekeeping_folder_name_${user}`; }

// ── Component ────────────────────────────────────────────────────────────────
const TimekeepingImport: React.FC = () => {
  const dispatch = useAppDispatch();
  const userName = useAppSelector(s => s.auth.userName);
  const selectedUnit = useAppSelector(s => s.auth.selectedUnit);

  const profiles = useAppSelector(s => s.timekeeping.profiles);
  const profilesLoading = useAppSelector(s => s.timekeeping.profilesLoading);
  const selectedProfileId = useAppSelector(s => s.timekeeping.selectedProfileId);
  const tasks = useAppSelector(s => s.timekeeping.tasks);
  const tasksLoading = useAppSelector(s => s.timekeeping.tasksLoading);
  const folderName = useAppSelector(s => s.timekeeping.folderName);
  const showPreview = useAppSelector(s => s.timekeeping.showPreview);
  const fromDateStr = useAppSelector(s => s.timekeeping.fromDate);
  const toDateStr = useAppSelector(s => s.timekeeping.toDate);
  const submitting = useAppSelector(s => s.timekeeping.submitting);
  const error = useAppSelector(s => s.timekeeping.error);

  const fromDate = dayjs(fromDateStr);
  const toDate = dayjs(toDateStr);

  // Non-serializable local state — cannot go in Redux
  const [folderHandles, setFolderHandles] = useState<Map<string, FileSystemFileHandle>>(new Map());
  const [folderFiles, setFolderFiles] = useState<Map<string, File>>(new Map());

  const usesFSA = typeof (window as any).showDirectoryPicker === 'function';
  const storedHandleRef = useRef<FileSystemDirectoryHandle | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const selectedProfile = profiles.find(p => p.id === selectedProfileId);

  const profileFileNameFormat = React.useMemo(() => {
    try {
      const raw = selectedProfile?.config;
      const cfg = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return (cfg as any)?.fileNameFormat ?? '';
    } catch { return ''; }
  }, [selectedProfile]);

  // Files that match the current date range + fileNameFormat
  const matchedFiles: Array<{ date: Dayjs; filename: string; found: boolean }> = React.useMemo(() => {
    if (!profileFileNameFormat || fromDate.isAfter(toDate)) return [];
    const result = [];
    let cursor = fromDate.startOf('day');
    const end = toDate.startOf('day');
    while (!cursor.isAfter(end)) {
      const filename = expectedFilename(profileFileNameFormat, cursor);
      const found = usesFSA ? folderHandles.has(filename) : folderFiles.has(filename);
      result.push({ date: cursor, filename, found });
      cursor = cursor.add(1, 'day');
    }
    return result;
  }, [profileFileNameFormat, fromDate, toDate, folderHandles, folderFiles, usesFSA]);

  const filesFound = matchedFiles.filter(m => m.found).length;
  const initialLoading =
    (profilesLoading && profiles.length === 0) ||
    (tasksLoading && tasks.length === 0);

  // ── Load profiles (cached — skips fetch if already in Redux) ──────────────
  useEffect(() => {
    dispatch(loadParserProfiles(userName, selectedUnit));
  }, [dispatch, userName, selectedUnit]);

  // ── Task list + polling ───────────────────────────────────────────────────
  const startPolling = useCallback((currentTasks: TaskResponse[]) => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (!currentTasks.some(needsPolling)) return;

    pollRef.current = setInterval(async () => {
      try {
        const res = await apiService.listTasks('TIMEKEEPING_IMPORT');
        const updated = res.data ?? [];
        dispatch(setTasks(updated));
        if (!updated.some(needsPolling)) {
          if (pollRef.current) clearInterval(pollRef.current);
        }
      } catch { /* retry next cycle */ }
    }, POLL_INTERVAL_MS);
  }, [dispatch]);

  useEffect(() => {
    (dispatch(fetchTasks()) as unknown as Promise<TaskResponse[]>).then(startPolling);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [dispatch, startPolling]);

  // ── Restore folder selection from previous session ────────────────────────
  useEffect(() => {
    if (!userName) return;
    const storedName = localStorage.getItem(folderNameKey(userName));
    if (storedName) dispatch(setFolderName(storedName));

    if (!usesFSA) return;
    loadHandleIDB(folderIDBKey(userName)).then(async handle => {
      if (!handle) return;
      storedHandleRef.current = handle;
      const perm = await (handle as any).queryPermission({ mode: 'read' });
      if (perm !== 'granted') return;
      const map = new Map<string, FileSystemFileHandle>();
      for await (const [name, h] of (handle as any).entries()) {
        if ((h as FileSystemHandle).kind === 'file') map.set(name, h as FileSystemFileHandle);
      }
      setFolderHandles(map);
      dispatch(setShowPreview(true));
    });
  }, [userName, usesFSA, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Folder selection ──────────────────────────────────────────────────────
  const handleSelectFolder = async () => {
    dispatch(setShowPreview(false));
    if (usesFSA) {
      try {
        const resolvedHandle = await (window as any).showDirectoryPicker({ mode: 'read' }) as FileSystemDirectoryHandle;
        storedHandleRef.current = resolvedHandle;
        dispatch(setFolderName(resolvedHandle.name));
        if (userName) {
          localStorage.setItem(folderNameKey(userName), resolvedHandle.name);
          await saveHandleIDB(folderIDBKey(userName), resolvedHandle);
        }
        const map = new Map<string, FileSystemFileHandle>();
        for await (const [name, handle] of (resolvedHandle as any).entries()) {
          if ((handle as FileSystemHandle).kind === 'file') map.set(name, handle as FileSystemFileHandle);
        }
        setFolderHandles(map);
        setFolderFiles(new Map());
        dispatch(setShowPreview(true));
      } catch {
        // User cancelled — ignore
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFolderChangeFallback = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) return;
    const firstPath = (files[0] as any).webkitRelativePath as string;
    const name = firstPath ? firstPath.split('/')[0] : 'Selected folder';
    dispatch(setFolderName(name));
    if (userName) {
      try { localStorage.setItem(folderNameKey(userName), name); } catch { /* non-fatal */ }
    }
    const map = new Map<string, File>();
    for (const file of files) map.set(file.name, file);
    setFolderFiles(map);
    setFolderHandles(new Map());
    dispatch(setShowPreview(true));
  };

  // ── Import ────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    const filesToUpload = matchedFiles.filter(m => m.found);
    if (!selectedProfileId || !profileFileNameFormat || filesToUpload.length === 0) return;

    dispatch(persistProfileSelection(selectedProfileId, userName, selectedUnit));
    dispatch(setShowPreview(false));
    dispatch(setSubmitting(true));
    dispatch(setError(''));

    const stagingPrefix = `timekeeping/staging/${getStagingSessionId()}/${Date.now()}/`;

    try {
      const s3Keys: string[] = [];
      for (const { filename } of filesToUpload) {
        const file: File = usesFSA
          ? await folderHandles.get(filename)!.getFile()
          : folderFiles.get(filename)!;
        const key = `${stagingPrefix}${filename}`;
        const res = await apiService.getUploadUrl(key, file.type || undefined);
        await fetch(res.data.url, {
          method: 'PUT',
          body: file,
          headers: file.type ? { 'Content-Type': file.type } : {},
        });
        s3Keys.push(res.data.key);
      }

      const label = buildLabel(fromDate, toDate);
      await apiService.submitTimekeepingImport({ s3Keys, parserProfileId: selectedProfileId, label });

      const loaded = await (dispatch(fetchTasks()) as unknown as Promise<TaskResponse[]>);
      startPolling(loaded);
    } catch (e: any) {
      dispatch(setError(e.message || 'Import failed'));
    } finally {
      dispatch(setSubmitting(false));
    }
  };

  const importDisabled =
    submitting ||
    !selectedProfileId ||
    !profileFileNameFormat ||
    filesFound === 0 ||
    fromDate.isAfter(toDate);

  return (
    <DashboardLayout>
      <Box p={3} maxWidth={900}>
        <Typography variant="h5" mb={3}>Timekeeping Import</Typography>

        {initialLoading ? (
          <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>
        ) : (<>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(setError(''))}>{error}</Alert>}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack spacing={2.5}>
            {/* Profile selector */}
            <FormControl fullWidth>
              <InputLabel>Parser Profile</InputLabel>
              <Select value={selectedProfileId} label="Parser Profile" onChange={e => dispatch(persistProfileSelection(e.target.value, userName, selectedUnit))}>
                {profiles.filter(p => p.workerType !== 'remote').map(p => (
                  <MenuItem key={p.id} value={p.id}>{p.name} — {p.fileType} / {p.workerType}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Folder picker */}
            <input
              ref={fileInputRef}
              type="file"
              // @ts-ignore — webkitdirectory is not in TS types but works in all major browsers
              webkitdirectory=""
              multiple
              style={{ display: 'none' }}
              onChange={handleFolderChangeFallback}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="outlined" startIcon={<FolderOpen />} onClick={handleSelectFolder}>
                Select Folder
              </Button>
              {folderName && (
                <Typography variant="body2" color="text.secondary">{folderName}</Typography>
              )}
            </Stack>

            {/* Date range */}
            <Stack direction="row" spacing={2}>
              <DatePicker
                label="From Date"
                value={fromDate}
                onChange={v => v && dispatch(setFromDate(v.format('YYYY-MM-DD')))}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <DatePicker
                label="To Date"
                value={toDate}
                onChange={v => v && dispatch(setToDate(v.format('YYYY-MM-DD')))}
                minDate={fromDate}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>

            {fromDate.isAfter(toDate) && (
              <Alert severity="warning">From date must not be after To date.</Alert>
            )}

            {/* Preview of matched files */}
            {showPreview && matchedFiles.length > 0 && (() => {
              const found = matchedFiles.filter(m => m.found);
              const missing = matchedFiles.filter(m => !m.found);
              return (
                <Stack spacing={1}>
                  {found.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        Found ({found.length}):
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {found.map(({ filename }) => (
                          <Chip key={filename} label={filename} size="small" color="success" />
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {missing.length > 0 && (
                    <Box>
                      <Typography variant="body2" color="text.secondary" mb={0.5}>
                        Not found ({missing.length}):
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {missing.map(({ filename }) => (
                          <Chip key={filename} label={filename} size="small" variant="outlined" />
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Stack>
              );
            })()}

            <Tooltip title={!profileFileNameFormat ? 'File Name Format is required — set it in the config' : filesFound === 0 && folderName ? 'No matching files found for the selected date range' : ''}>
              <span>
                <Button variant="contained" onClick={handleImport} disabled={importDisabled}>
                  {submitting ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
                  Import
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Paper>

        {/* Task history table */}
        <Typography variant="h6" mb={1}>Import Tasks</Typography>
        <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Records</TableCell>
                  <TableCell>Message</TableCell>
                  <TableCell>Started</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No import tasks yet.</TableCell>
                  </TableRow>
                ) : tasks.map(t => {
                  const out = t.outputData as any;
                  const lastLog = t.logs?.at(-1) ?? '';
                  return (
                    <React.Fragment key={t.taskId}>
                      <TableRow>
                        <TableCell sx={{ maxWidth: 240, wordBreak: 'break-word' }}>
                          {t.label || t.taskId.slice(0, 8)}
                        </TableCell>
                        <TableCell>
                          <Chip label={t.status} color={statusColor(t.status)} size="small" />
                        </TableCell>
                        <TableCell>
                          {out ? `${out.parsed ?? '–'} / ${out.written ?? '–'} / ${out.skipped ?? '–'}` : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300, wordBreak: 'break-word', fontSize: 12, color: 'text.secondary' }}>
                          {lastLog}
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                          {new Date(t.createdAt).toLocaleString()}
                        </TableCell>
                      </TableRow>
                      {t.status === 'PROCESSING' && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ py: 0 }}>
                            <LinearProgress variant="determinate" value={t.progressPercent} />
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>)}
      </Box>
    </DashboardLayout>
  );
};

export default TimekeepingImport;
